import React, { useRef, useState } from "react";
import { Stack, TextField, Typography, Button } from "@mui/material";
import ImagePickerDialog from "../ImagePickerDialog";
import HtmlEditor from "./HtmlEditor";

const normalizeOptions = (options) =>
    Array.isArray(options)
        ? options.map((o) => ({
            id: String(o?.id || "").trim(),
            text: String(o?.text || ""),
        }))
        : [];

const QuestionFormTest = ({ value, onChange }) => {
    const v = value || {};
    const options = normalizeOptions(v.options);

    const [pickerOpen, setPickerOpen] = useState(false);
    const questionRef = useRef(null);

    function updateOption(idx, patch) {
        const next = options.map((o, i) => (i === idx ? { ...o, ...patch } : o));
        onChange({ ...v, options: next });
    }

    function addOption() {
        const next = [...options, { id: "", text: "" }];
        onChange({ ...v, options: next });
    }

    function removeOption(idx) {
        const next = options.filter((_, i) => i !== idx);
        onChange({ ...v, options: next });
    }

    function onPickImage(img) {
        const caption = img.originalFilename || "Зображення";

        const block =
            `\n<figure style="text-align:center;">` +
            `\n  <img src="${img.url}" style="width:100%; max-width:100%; height:auto;" alt="${caption}" />` +
            `\n  <figcaption class="imgcaption">${caption}</figcaption>` +
            `\n</figure>\n`;

        questionRef.current?.insertHtml(block);
        setPickerOpen(false);
    }

    return (
        <Stack spacing={2}>
            <TextField
                label="Підзаголовок"
                value={v.subtitle || ""}
                onChange={(e) => onChange({ ...v, subtitle: e.target.value })}
                helperText="Необов'язково. Показується під назвою модуля."
                fullWidth
            />

            <HtmlEditor
                label="Питання (HTML)"
                value={v.question || ""}
                onChange={(question) => onChange({ ...v, question })}
                helperText="Текст питання. Можна використовувати HTML і вставляти зображення."
                minHeight={120}
                ref={questionRef}
            />

            <Button variant="outlined" onClick={() => setPickerOpen(true)}>
                Вставити зображення
            </Button>

            <TextField
                label="Зображення до питання"
                value={v.imgPath || ""}
                onChange={(e) => onChange({ ...v, imgPath: e.target.value })}
                helperText="Необов'язково. URL зображення, яке буде показане разом із питанням."
                fullWidth
            />

            <TextField
                label="Відео до питання"
                value={v.videoPath || ""}
                onChange={(e) => onChange({ ...v, videoPath: e.target.value })}
                helperText="Необов'язково. Посилання на iframe/embed відео."
                fullWidth
            />

            <TextField
                label="CSS-клас відображення"
                value={v.addClass || ""}
                onChange={(e) => onChange({ ...v, addClass: e.target.value })}
                helperText="Службове поле. Заповнюйте лише якщо потрібен спеціальний стиль."
                fullWidth
            />

            <TextField
                label="Правильна відповідь"
                value={v.correctAnswer || ""}
                onChange={(e) => onChange({ ...v, correctAnswer: e.target.value })}
                placeholder="А / Б / В / Г або 1 / 2 ..."
                helperText="Вкажіть ID правильного варіанта відповіді."
                fullWidth
            />

            <Typography variant="subtitle2" sx={{ mt: 1 }}>
                Варіанти відповіді
            </Typography>

            {options.map((opt, idx) => (
                <Stack key={idx} direction={{ xs: "column", sm: "row" }} spacing={1}>
                    <TextField
                        label="ID"
                        value={opt.id}
                        onChange={(e) => updateOption(idx, { id: e.target.value })}
                        helperText="Код варіанта."
                        sx={{ width: { sm: 120 } }}
                    />
                    <HtmlEditor
                        label="Текст варіанта (HTML)"
                        value={opt.text}
                        onChange={(text) => updateOption(idx, { text })}
                        helperText="Текст, який побачить учень."
                        minHeight={56}
                    />
                    <TextField
                        value="✕"
                        onClick={() => removeOption(idx)}
                        sx={{
                            width: { sm: 60 },
                            "& input": { cursor: "pointer", textAlign: "center" },
                        }}
                        inputProps={{ readOnly: true }}
                    />
                </Stack>
            ))}

            <TextField
                value="+ додати варіант"
                onClick={addOption}
                inputProps={{ readOnly: true }}
                sx={{ "& input": { cursor: "pointer" } }}
            />

            <ImagePickerDialog
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                onPick={onPickImage}
            />
        </Stack>
    );
};

export default QuestionFormTest;
